import { useState, useEffect, useCallback } from 'react';
import { Flex, Box, VStack, Heading, Button } from '@chakra-ui/react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled/macro';

import { generatePowerCubeFile } from '../services/powerCube';
import { downloadFile } from '../services/items';
import { getDashboardSummary } from './../services/studies';

import { ReactComponent as CubeIcon } from '../icons/cube.svg';

import DashboardLayoutWrapper from '../components/dashboard/DashboardLayoutWrapper';
import DashboardStats from '../components/dashboard/DashboardStats';
import DashboardSidebarCard from '../components/dashboard/DashboardSidebarCard';
import StudyGrid from '../components/studies/StudyGrid';
import Logo from '../components/utils/Logo';

import useHistoryPush from '../hooks/useHistoryPush';

const Dashboard = () => {
    const intl = useIntl();
    const historyPush = useHistoryPush();

    const [noDataFound, setNoDataFound] = useState(false);
    const [dashboardSummary, setDashboardSummary] = useState({});

    const schemaGroupId = useSelector((state) => state.connections.schemaGroupId);

    const fetchSchemaGroupId = useCallback(async () => {
        const dashbordResult = await getDashboardSummary(schemaGroupId);

        setDashboardSummary(dashbordResult);
    }, [schemaGroupId]);

    useEffect(() => {
        fetchSchemaGroupId().catch(() => setNoDataFound(true));
    }, [fetchSchemaGroupId]);

    const stats = [
        {
            label: intl.formatMessage({ id: 'dashboard_studies_running' }),
            isLabelVisible: true,
            value: intl.formatMessage({ id: 'dashboard_n_studies' }, { n: dashboardSummary.runningStudies }),
            isValueCritical: false,
            description: intl.formatMessage(
                { id: 'dashboard_you_have_n_running' },
                { n: dashboardSummary.startedByYou }
            ),
            hasDivider: false,
        },
        {
            label: intl.formatMessage({ id: 'dashboard_db_space' }),
            isLabelVisible: true,
            value: intl.formatMessage(
                { id: 'dashboard_percentage_full' },
                { percentage: dashboardSummary.percentUsed === null ? '--' : Math.round(dashboardSummary.percentUsed) }
            ),
            isValueCritical: true,
            description: intl.formatMessage(
                { id: 'dashboard_db_space_ratio' },
                {
                    usedSize: (dashboardSummary.used === null ? '--' : Math.round(dashboardSummary.used)) + ' GB',
                    remainingSize:
                        (dashboardSummary.remaining === null ? '--' : Math.round(dashboardSummary.remaining)) + ' GB',
                }
            ),
            hasDivider: false,
        },
    ];

    const exportPowerCubeData = async () => {
        const fileInfo = await generatePowerCubeFile();

        downloadFile(fileInfo);
    };

    return (
        <DashboardLayoutWrapper
            header={
                <Box bg="white" px={6} pb={6} borderBottom="1px" borderColor="gray.100">
                    <Flex align="center" wrap="wrap">
                        <LogoWrapper>
                            <Logo width={185} />
                        </LogoWrapper>

                        <Flex flex="1">
                            {noDataFound ? (
                                intl.formatMessage({ id: 'common_no_data_found' })
                            ) : Object.keys(dashboardSummary).length === 0 ? (
                                intl.formatMessage({ id: 'common_loading' })
                            ) : (
                                <DashboardStats stats={stats} />
                            )}
                        </Flex>

                        <Button
                            leftIcon={<CubeIcon />}
                            onClick={exportPowerCubeData}
                            size="sm"
                            w={{ base: '100%', md: 'auto' }}
                            mt={{ base: '16px', md: '0' }}
                        >
                            <FormattedMessage id="power_cube_button_text" />
                        </Button>
                    </Flex>
                </Box>
            }
            sidebar={
                <>
                    <Heading as="h3" variant="h3" pb={7}>
                        <FormattedMessage id="dashboard_quick_actions" />
                    </Heading>

                    <VStack spacing={10}>
                        <DashboardSidebarCard
                            title={<FormattedMessage id="dashboard_edit_portfolio" />}
                            description={<FormattedMessage id="dashboard_edit_portfolio_desc" />}
                            buttonText={<FormattedMessage id="dashboard_portfolios" />}
                            onClick={() => historyPush('/portfolios')}
                        />

                        <DashboardSidebarCard
                            title={<FormattedMessage id="dashboard_add_pricing_location" />}
                            description={<FormattedMessage id="dashboard_add_pricing_location_desc" />}
                            buttonText={<FormattedMessage id="dashboard_add_pricing_location_btn" />}
                            isDisabled
                        />

                        <DashboardSidebarCard
                            title={<FormattedMessage id="dashboard_run_a_study_btn" />}
                            description={<FormattedMessage id="dashboard_run_a_study_desc" />}
                            buttonText={<FormattedMessage id="dashboard_run_a_study_btn" />}
                            onClick={() => historyPush('/run-a-study')}
                        />
                    </VStack>
                </>
            }
            content={<StudyGrid />}
            hasFooter={false}
        />
    );
};

const LogoWrapper = styled.div`
    display: none;
    flex: 1 1 100%;
    max-width: 320px;

    @media (min-width: 899px) {
        display: block;
    }
`;

export default Dashboard;
